import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Users, Plus, Settings, FolderOpen, Activity, 
  Mail, UserPlus, Crown, Shield, Eye, Pencil,
  MoreVertical, Trash2, Check, X, Clock
} from 'lucide-react';

const StudioWorkspace = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [studio, setStudio] = useState(null);
  const [role, setRole] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [members, setMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activeTab, setActiveTab] = useState('projects');
  
  // Modal states
  const [showCreateStudio, setShowCreateStudio] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [showCreateProject, setShowCreateProject] = useState(false);
  
  // Form states
  const [studioForm, setStudioForm] = useState({ studio_name: '', description: '' });
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'designer', message: '' });
  const [projectForm, setProjectForm] = useState({ 
    name: '', description: '', client_name: '', client_email: '', project_type: '', location: '' 
  });

  const fetchStudio = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/studios/my-studio`, { withCredentials: true });
      setStudio(res.data.studio);
      setRole(res.data.role);
      setPermissions(res.data.permissions || {});
      
      if (res.data.studio) {
        // Fetch members, projects, and activities
        const [membersRes, projectsRes, activitiesRes] = await Promise.all([
          axios.get(`${API}/studios/${res.data.studio.studio_id}/members`, { withCredentials: true }),
          axios.get(`${API}/studios/${res.data.studio.studio_id}/projects`, { withCredentials: true }),
          axios.get(`${API}/studios/${res.data.studio.studio_id}/activity`, { withCredentials: true })
        ]);
        
        setMembers(membersRes.data.members || []);
        setPendingInvitations(membersRes.data.pending_invitations || []);
        setProjects(projectsRes.data.projects || []);
        setActivities(activitiesRes.data.activities || []);
      }
    } catch (err) {
      console.error('Error fetching studio:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudio();
  }, [fetchStudio]);

  const handleCreateStudio = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/studios/create`, studioForm, { withCredentials: true });
      toast.success('Studio creato con successo!');
      setShowCreateStudio(false);
      fetchStudio();
    } catch (err) {
      toast.error(err.response?.data?.detail?.upgrade_message || 'Errore nella creazione dello studio');
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/studios/${studio.studio_id}/invite`, inviteForm, { withCredentials: true });
      toast.success('Invito inviato!');
      setShowInviteMember(false);
      setInviteForm({ email: '', role: 'designer', message: '' });
      fetchStudio();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'invio dell\'invito');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/studios/${studio.studio_id}/projects`, projectForm, { withCredentials: true });
      toast.success('Progetto creato!');
      setShowCreateProject(false);
      setProjectForm({ name: '', description: '', client_name: '', client_email: '', project_type: '', location: '' });
      fetchStudio();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione del progetto');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    try {
      await axios.delete(`${API}/studios/${studio.studio_id}/invitations/${invitationId}`, { withCredentials: true });
      toast.success('Invito annullato');
      fetchStudio();
    } catch (err) {
      toast.error('Errore nell\'annullamento dell\'invito');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Sei sicuro di voler rimuovere questo membro?')) return;
    try {
      await axios.delete(`${API}/studios/${studio.studio_id}/members/${userId}`, { withCredentials: true });
      toast.success('Membro rimosso');
      fetchStudio();
    } catch (err) {
      toast.error('Errore nella rimozione del membro');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-amber-500" />;
      case 'project_manager': return <Shield className="w-4 h-4 text-[var(--gold)]" />;
      case 'designer': return <Pencil className="w-4 h-4 text-green-500" />;
      case 'viewer': return <Eye className="w-4 h-4 text-gray-500" />;
      default: return null;
    }
  };

  const getRoleName = (role) => {
    const names = {
      'owner': 'Proprietario',
      'project_manager': 'Project Manager',
      'designer': 'Designer',
      'viewer': 'Viewer'
    };
    return names[role] || role;
  };

  const getStatusBadge = (status) => {
    const styles = {
      'draft': 'bg-gray-100 text-gray-700',
      'active': 'bg-green-100 text-green-700',
      'on_hold': 'bg-yellow-100 text-yellow-700',
      'completed': 'bg-[var(--gold)]/20 text-[var(--gold)]',
      'archived': 'bg-gray-100 text-gray-500'
    };
    const names = {
      'draft': 'Bozza',
      'active': 'Attivo',
      'on_hold': 'In Pausa',
      'completed': 'Completato',
      'archived': 'Archiviato'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.draft}`}>
        {names[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Footer />
      </div>
    );
  }

  // No studio yet - show create option
  if (!studio) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Studio Workspace</h1>
            <p className="text-muted-foreground mb-8">
              Crea il tuo studio per collaborare con il tuo team, gestire progetti condivisi e lavorare insieme sui materiali.
            </p>
            <Button 
              size="lg" 
              onClick={() => setShowCreateStudio(true)}
              data-testid="create-studio-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Crea il tuo Studio
            </Button>
          </div>
        </main>

        {/* Create Studio Modal */}
        {showCreateStudio && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <h2 className="text-xl font-bold mb-4">Crea Studio</h2>
              <form onSubmit={handleCreateStudio} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome Studio *</label>
                  <Input
                    value={studioForm.studio_name}
                    onChange={(e) => setStudioForm({ ...studioForm, studio_name: e.target.value })}
                    placeholder="Es: Studio Architettura Milano"
                    required
                    data-testid="studio-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Descrizione</label>
                  <Input
                    value={studioForm.description}
                    onChange={(e) => setStudioForm({ ...studioForm, description: e.target.value })}
                    placeholder="Descrizione breve dello studio"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowCreateStudio(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" data-testid="submit-create-studio">
                    Crea Studio
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Studio Header */}
        <div className="bg-white rounded-xl p-6 mb-6 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold" data-testid="studio-name">{studio.studio_name}</h1>
                <p className="text-muted-foreground">{studio.description || 'Il tuo workspace collaborativo'}</p>
                <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {members.length + 1} membri
                  </span>
                  <span className="flex items-center gap-1">
                    <FolderOpen className="w-4 h-4" />
                    {projects.length} progetti
                  </span>
                </div>
              </div>
            </div>
            {permissions.manage_team && (
              <Button variant="outline" size="sm">
                <Settings className="w-4 h-4 mr-2" />
                Impostazioni
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('projects')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'projects' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-projects"
          >
            <FolderOpen className="w-4 h-4 inline mr-2" />
            Progetti
          </button>
          <button
            onClick={() => setActiveTab('team')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'team' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-team"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Team
          </button>
          <button
            onClick={() => setActiveTab('activity')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'activity' 
                ? 'border-primary text-primary' 
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="tab-activity"
          >
            <Activity className="w-4 h-4 inline mr-2" />
            Attività
          </button>
        </div>

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Progetti Studio</h2>
              {permissions.create_projects && (
                <Button onClick={() => setShowCreateProject(true)} data-testid="create-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Progetto
                </Button>
              )}
            </div>
            
            {projects.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <FolderOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessun progetto ancora. Crea il primo progetto del team!</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map((project) => (
                  <div 
                    key={project.project_id}
                    className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/studio/projects/${project.project_id}`)}
                    data-testid={`project-card-${project.project_id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold">{project.name}</h3>
                      {getStatusBadge(project.status)}
                    </div>
                    {project.client_name && (
                      <p className="text-sm text-muted-foreground mb-2">
                        Cliente: {project.client_name}
                      </p>
                    )}
                    {project.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                        {project.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{project.boards_count || 0} board</span>
                      <span>{project.assigned_members_count || 0} membri assegnati</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Team Tab */}
        {activeTab === 'team' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Membri del Team</h2>
              {permissions.manage_team && (
                <Button onClick={() => setShowInviteMember(true)} data-testid="invite-member-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invita Membro
                </Button>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Owner */}
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                    <Crown className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium">{user?.email}</p>
                    <p className="text-sm text-muted-foreground">Proprietario</p>
                  </div>
                </div>
                <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">Owner</span>
              </div>

              {/* Members */}
              {members.map((member) => (
                <div key={member.member_id} className="p-4 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      {getRoleIcon(member.role)}
                    </div>
                    <div>
                      <p className="font-medium">{member.user_name || member.user_email}</p>
                      <p className="text-sm text-muted-foreground">{getRoleName(member.role)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                      {getRoleName(member.role)}
                    </span>
                    {permissions.manage_team && (
                      <button 
                        onClick={() => handleRemoveMember(member.user_id)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Pending Invitations */}
              {pendingInvitations.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-gray-50 text-sm font-medium text-muted-foreground">
                    Inviti in attesa
                  </div>
                  {pendingInvitations.map((invite) => (
                    <div key={invite.invitation_id} className="p-4 border-b flex items-center justify-between bg-yellow-50/30">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <p className="font-medium">{invite.email}</p>
                          <p className="text-sm text-muted-foreground">
                            Invitato come {getRoleName(invite.role)}
                          </p>
                        </div>
                      </div>
                      {permissions.manage_team && (
                        <button 
                          onClick={() => handleCancelInvitation(invite.invitation_id)}
                          className="text-sm text-red-600 hover:underline"
                        >
                          Annulla
                        </button>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Attività Recenti</h2>
            
            {activities.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessuna attività recente</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm">
                {activities.map((activity, idx) => (
                  <div 
                    key={activity.activity_id} 
                    className={`p-4 flex items-start gap-3 ${idx !== activities.length - 1 ? 'border-b' : ''}`}
                  >
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Activity className="w-4 h-4 text-gray-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm">
                        <span className="font-medium">{activity.user_name}</span>
                        {' '}{activity.description}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {activity.project_name} • {new Date(activity.created_at).toLocaleString('it-IT')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Invite Member Modal */}
      {showInviteMember && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Invita Membro</h2>
            <form onSubmit={handleInviteMember} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="email@esempio.com"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ruolo *</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full border rounded-lg p-2"
                  data-testid="invite-role-select"
                >
                  <option value="designer">Designer</option>
                  <option value="project_manager">Project Manager</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Messaggio (opzionale)</label>
                <Input
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                  placeholder="Un messaggio personale..."
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowInviteMember(false)}>
                  Annulla
                </Button>
                <Button type="submit" data-testid="submit-invite">
                  <Mail className="w-4 h-4 mr-2" />
                  Invia Invito
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showCreateProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Nuovo Progetto</h2>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nome Progetto *</label>
                <Input
                  value={projectForm.name}
                  onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                  placeholder="Es: Villa Rossi - Milano"
                  required
                  data-testid="project-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descrizione</label>
                <Input
                  value={projectForm.description}
                  onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })}
                  placeholder="Descrizione del progetto..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nome Cliente</label>
                  <Input
                    value={projectForm.client_name}
                    onChange={(e) => setProjectForm({ ...projectForm, client_name: e.target.value })}
                    placeholder="Mario Rossi"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email Cliente</label>
                  <Input
                    type="email"
                    value={projectForm.client_email}
                    onChange={(e) => setProjectForm({ ...projectForm, client_email: e.target.value })}
                    placeholder="cliente@email.com"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo Progetto</label>
                  <select
                    value={projectForm.project_type}
                    onChange={(e) => setProjectForm({ ...projectForm, project_type: e.target.value })}
                    className="w-full border rounded-lg p-2"
                  >
                    <option value="">Seleziona...</option>
                    <option value="residential">Residenziale</option>
                    <option value="commercial">Commerciale</option>
                    <option value="hospitality">Hospitality</option>
                    <option value="retail">Retail</option>
                    <option value="office">Ufficio</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Location</label>
                  <Input
                    value={projectForm.location}
                    onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })}
                    placeholder="Milano, Italia"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateProject(false)}>
                  Annulla
                </Button>
                <Button type="submit" data-testid="submit-project">
                  Crea Progetto
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default StudioWorkspace;
